"use client";

import { Badge, Button, cn, Loader } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { ChevronRight, History, Receipt, ScanBarcodeIcon } from "lucide-react";
import type {
  PosReturnSummary,
  PosSaleListItem,
} from "@/modules/merchant/presentation/api/client";
import {
  saleStatusBadgeVariant,
  saleStatusRowAccent,
  statusLabel,
} from "@/modules/merchant/presentation/ui/shop-pos/pos-sale-status";
import { formatPrice } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

export type ShopPosSalesHistoryListProps = {
  shopId: string | undefined;
  items: PosSaleListItem[];
  /** ສະຫຼຸບ ຄືນ/ປ່ຽນ ຕໍ່ບິນ (key = saleId) — ສະແດງໃນ tab ຄືນ/ປ່ຽນ */
  returnSummaries?: Record<string, PosReturnSummary>;
  loading: boolean;
  total: number;
  offset: number;
  limit: number;
  pageEnd: number;
  hasPrev: boolean;
  hasNext: boolean;
  onSelect: (id: string) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  /** ສະແດງປຸ່ມ ຄືນ/ປ່ຽນ ໃນແຖວ (paid/partial) */
  canReturn?: boolean;
  onReturn?: (id: string) => void;
  onExchange?: (id: string) => void;
};

export function ShopPosSalesHistoryList({
  shopId,
  items,
  returnSummaries,
  loading,
  total,
  offset,
  pageEnd,
  hasPrev,
  hasNext,
  onSelect,
  onPrevPage,
  onNextPage,
  canReturn = false,
  onReturn,
  onExchange,
}: ShopPosSalesHistoryListProps) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Desktop table header */}
      <div className="hidden border-border border-b bg-muted/20 text-muted-foreground text-xs uppercase tracking-wide sm:flex sm:items-center">
        <div className="grid flex-1 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-4 px-5 py-2.5">
          <span>ເລກບິນ / ວັນທີ</span>
          <span className="text-center">ຍອດ</span>
          <span className="text-end">ສະຖານະ</span>
        </div>
        <div className="w-44 shrink-0" aria-hidden />
      </div>

      <div className="divide-y divide-border">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-muted/60 text-[hsl(210_90%_48%)]">
              <History className="size-7" aria-hidden />
            </div>
            <p className="font-heading font-semibold text-foreground text-lg">
              ບໍ່ພົບບິນ
            </p>
            <p className="mt-1 max-w-sm text-muted-foreground text-sm">
              ລອງປ່ຽນຕົວກອງ ຫຼື ກັບໄປຂາຍເພື່ອສ້າງບິນໃໝ່
            </p>
            {shopId ? (
              <Link
                to="/app/shops/$shopId/pos"
                params={{ shopId }}
                className={cn(
                  "mt-5 inline-flex h-10 items-center gap-2 rounded-lg border px-4 font-medium text-sm transition-colors",
                  "border-emerald-500/35 bg-emerald-500/10 text-emerald-950 hover:bg-emerald-500/15 dark:text-emerald-100",
                )}
              >
                <ScanBarcodeIcon className="size-4" aria-hidden />
                ກັບໄປຂາຍ
              </Link>
            ) : null}
          </div>
        ) : (
          items.map((row) => (
            <div
              key={row.id}
              className={cn(
                "group flex w-full items-stretch border-l-[3px] transition-colors",
                saleStatusRowAccent(row.status),
                "hover:bg-muted/40",
              )}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 text-start"
                onClick={() => onSelect(row.id)}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2 px-4 py-3.5 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center sm:gap-4 sm:px-5 sm:py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(210_90%_48%)]/10 text-[hsl(210_90%_48%)]">
                      <Receipt className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-semibold text-card-foreground text-sm sm:text-base">
                          #{row.saleNumber}
                        </span>
                        <Badge
                          variant={saleStatusBadgeVariant(row.status)}
                          className="text-[10px] sm:hidden"
                        >
                          {statusLabel(row.status)}
                        </Badge>
                        {row.exchanged ? (
                          <Badge variant="warning" className="text-[10px]">
                            ປ່ຽນແລ້ວ
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-muted-foreground text-xs">
                        {formatDateTimeLocal(row.createdAt)}
                        {row.holdExpiresAt ? (
                          <span className="text-amber-700 dark:text-amber-400">
                            {" "}
                            · ໝົດ {formatDateTimeLocal(row.holdExpiresAt)}
                          </span>
                        ) : null}
                      </p>
                      {row.discountTotal > 0 || row.taxTotal > 0 ? (
                        <p className="mt-1 hidden text-[11px] text-muted-foreground/80 sm:block">
                          {row.discountTotal > 0
                            ? `ຫຼຸດ ${formatPrice(row.discountTotal, row.currency)}`
                            : null}
                          {row.discountTotal > 0 && row.taxTotal > 0
                            ? " · "
                            : null}
                          {row.taxTotal > 0
                            ? `VAT ${formatPrice(row.taxTotal, row.currency)}`
                            : null}
                        </p>
                      ) : null}
                      <ReturnSummaryLine summary={returnSummaries?.[row.id]} />
                    </div>
                  </div>

                  {/* ຍອດ (total) — ຢູ່ກາງ ຫຼັງ ເລກບິນ/ວັນທີ */}
                  <div className="flex items-center sm:justify-center">
                    <p className="font-bold font-heading text-base text-card-foreground tabular-nums tracking-tight sm:text-lg">
                      {formatPrice(row.total, row.currency)}
                    </p>
                  </div>

                  {/* ສະຖານະ (status) + chevron */}
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <Badge
                      variant={saleStatusBadgeVariant(row.status)}
                      className="hidden text-[10px] sm:inline-flex"
                    >
                      {statusLabel(row.status)}
                    </Badge>
                    <ChevronRight
                      className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground"
                      aria-hidden
                    />
                  </div>
                </div>
              </button>

              {/* Action — ຈອງຄວາມກວ້າງຄົງທີ່ທຸກແຖວ ໃຫ້ຄໍລຳຕົງກັນ */}
              <div className="flex w-28 shrink-0 flex-col items-center justify-center gap-1.5 px-2 py-2 sm:w-44 sm:flex-row sm:px-3">
                {canReturn &&
                (row.status === "paid" || row.status === "partial") ? (
                  row.fullyReturned ? (
                    <span className="text-muted-foreground text-xs">
                      ຄືນຄົບແລ້ວ
                    </span>
                  ) : (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="h-8"
                        onClick={() => onReturn?.(row.id)}
                      >
                        ຄືນ
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="h-8"
                        onClick={() => onExchange?.(row.id)}
                      >
                        ປ່ຽນ
                      </Button>
                    </>
                  )
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      {!loading && items.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-border border-t bg-muted/10 px-4 py-3 text-sm sm:px-5">
          <span className="text-muted-foreground tabular-nums">
            {total === 0 ? "0" : `${offset + 1}–${pageEnd}`} ຈາກ {total}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasPrev}
              onClick={onPrevPage}
            >
              ກ່ອນໜ້າ
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={onNextPage}
            >
              ຖັດໄປ
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** ສະຫຼຸບ ຄືນ/ປ່ຽນ ຕໍ່ບິນ — "ຄືນ: A x2 → ໃໝ່: B x1" */
function ReturnSummaryLine({ summary }: { summary?: PosReturnSummary }) {
  if (!summary) return null;
  const fmt = (arr: PosReturnSummary["oldItems"]) =>
    arr.length > 0
      ? arr.map((i) => `${i.productName} x${i.qty}`).join(", ")
      : "—";
  return (
    <p className="mt-1 text-[11px] text-muted-foreground">
      <span className="font-medium text-red-600">ຄືນ:</span>{" "}
      {fmt(summary.oldItems)}
      {summary.type === "exchange" ? (
        <>
          {" → "}
          <span className="font-medium text-orange-600">ໃໝ່:</span>{" "}
          {fmt(summary.newItems)}
        </>
      ) : null}
    </p>
  );
}
